import React from 'react';
import type { QueryFunction, UseQueryOptions } from 'react-query';
import {
  useInfiniteQuery,
  useIsFetching,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { useRouter } from 'next/router';

import { getSnowdonJwt } from '~/domains/auth/utils';
import { fetchOAIData, getRequestUrl } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type {
  OAIError,
  OAIParameters,
  OAIPathKeysByMethod,
  OAIResponse,
  OAISchema,
  OAIUpdateMethods,
} from '~/types/openapi';
import type {
  CustomQueryKey,
  UseOAIInfiniteQueryProps,
  UseOAIMutationProps,
  UseOAIMutationVariables,
  UseOAIQueryProps,
} from '~/types/reactQuery';
import { logError } from '~/utils/datadog';
import { randomNumber } from '~/utils/number';
import { isClient, isServer } from '~/utils/ssr';
import type { StorageKeyType } from '~/utils/storage';
import { getStorageData, removeStorageData } from '~/utils/storage';

import { useUserUUID } from './useUserUUID';

export function useOAIQuery<
  TQueryKey extends OAIPathKeysByMethod<'get'>,
  TVariables extends OAIParameters<TQueryKey, 'get'>,
  TData extends OAIResponse<TQueryKey, 'get'>,
  TError extends OAIApiError,
>({
  queryKey,
  queryOptions,
  variables,
  fetchOptions,
}: UseOAIQueryProps<TQueryKey, TVariables, TData, TError>) {
  const uuidHeaderOption = useUserUUID();

  return useQuery(
    variables ? [queryKey, variables] : queryKey,
    async ({ signal }) => {
      if (isServer()) {
        return {} as TData;
      }

      const data = await fetchOAIData<TData>({
        queryKey,
        variables,
        fetchOptions: {
          signal,
          ...fetchOptions,
          headers: {
            ...fetchOptions?.headers,
            'X-Client-Auth-Token': getSnowdonJwt(),
            ...uuidHeaderOption,
          },
        },
      });

      return data;
    },
    queryOptions,
  );
}

export function useOAIInvalidateQueries<
  TQueryKey extends OAIPathKeysByMethod<'get'>,
>() {
  const queryClient = useQueryClient();
  return (
    ...keys: (
      | TQueryKey
      | [TQueryKey, Record<string, unknown>]
      | Readonly<CustomQueryKey>
      | CustomQueryKey
    )[]
  ) => {
    const promises = keys.map((key) => queryClient.invalidateQueries(key));
    return Promise.all(promises);
  };
}

export function useOAICancelQueries<
  TQueryKey extends OAIPathKeysByMethod<'get'>,
>() {
  const queryClient = useQueryClient();
  return (
    ...keys: (
      | TQueryKey
      | [TQueryKey, Record<string, unknown>]
      | Readonly<CustomQueryKey>
      | CustomQueryKey
    )[]
  ) => {
    const promises = keys.map((key) => queryClient.cancelQueries(key));
    return Promise.all(promises);
  };
}

export function useOAIRemoveQueries<
  TQueryKey extends OAIPathKeysByMethod<'get'>,
>() {
  const queryClient = useQueryClient();
  return (...keys: (TQueryKey | [TQueryKey, Record<string, unknown>])[]) =>
    keys.forEach((key) => {
      void queryClient.removeQueries(key);
    });
}

export function useOAIMutation<
  TMethod extends OAIUpdateMethods,
  TMutationKey extends OAIPathKeysByMethod<TMethod>,
  TVariables extends UseOAIMutationVariables<TMethod, TMutationKey>,
  TData extends OAIResponse<TMutationKey, TMethod>,
>({
  method,
  mutationKey,
  options,
  fetchOptions,
}: UseOAIMutationProps<TMethod, TMutationKey, TVariables, TData>) {
  const uuidHeaderOption = useUserUUID();
  return useMutation(async (mutationVariables) => {
    const { requestBody, formData, ...variables } = (mutationVariables ||
      {}) as {
      requestBody?: Record<string, unknown>;
      formData?: FormData;
    } & Record<string, unknown>;

    const body = requestBody ? JSON.stringify(requestBody) : formData || null;

    const response = await fetch(getRequestUrl(mutationKey, variables), {
      ...fetchOptions,
      method: method.toUpperCase(),
      body,
      headers: {
        ...fetchOptions?.headers,
        // FormData 를 서버에 요청 할 땐 자동으로 content-type 이 자동으로 설정될 수 있도록.
        ...(requestBody ? { 'Content-Type': 'application/json' } : {}),
        'X-Client-Auth-Token': getSnowdonJwt(),
        'X-Market-Front-Page': isClient() ? window.location.href : 'server',
        ...uuidHeaderOption,
      },
      credentials: 'include' as RequestCredentials,
    });

    if (!response.ok) {
      const errResponse = (await response.json()) as OAIError;
      const error = new OAIApiError(response.status, errResponse, response.url);
      logError(error);
      throw error;
    }

    const json = (await response.json()) as TData;
    return json;
  }, options);
}

export function useOAIOptimisticMutation<
  TMethod extends OAIUpdateMethods,
  TMutationKey extends OAIPathKeysByMethod<TMethod>,
  TVariables extends UseOAIMutationVariables<TMethod, TMutationKey>,
  TQueryKey extends OAIPathKeysByMethod<'get'>,
  TData extends OAIResponse<TQueryKey, 'get'>,
>({
  method,
  mutationKey,
  updater,
  updateQueryKey,
  invalidateQueryKeys,
}: {
  method: TMethod;
  mutationKey: TMutationKey;
  updateQueryKey: TQueryKey;
  invalidateQueryKeys: Parameters<ReturnType<typeof useOAIInvalidateQueries>>;
  updater: (data: { old: TData; variables: TVariables }) => TData;
}) {
  const mutateIdRef = React.useRef<string>();
  const queryClient = useQueryClient();
  const cancelQueries = useOAICancelQueries();
  const invalidateQueries = useOAIInvalidateQueries();
  const { mutate } = useOAIMutation({
    method,
    mutationKey,
    options: {
      onMutate: async (variables) => {
        mutateIdRef.current = randomNumber().toString(36).substring(2, 10);
        await cancelQueries(...invalidateQueryKeys);
        const currentData = queryClient.getQueryData(updateQueryKey);
        queryClient.setQueryData(updateQueryKey, (old) =>
          updater({
            old: old as TData,
            variables: variables as TVariables,
          }),
        );

        return { mutateId: mutateIdRef.current, prev: currentData };
      },
      onSettled: (_, error, __, context) => {
        const { mutateId, prev } = context as { mutateId: string; prev: TData };

        if (mutateId !== mutateIdRef.current) {
          return;
        }

        if (error) {
          queryClient.setQueryData(updateQueryKey, prev);
        }

        void invalidateQueries(...invalidateQueryKeys);
      },
    },
  });

  return mutate;
}

export function useOAIInfiniteQuery<
  TQueryKey extends OAIPathKeysByMethod<'get'>,
  TContent extends Record<string, unknown>,
>({
  queryKey,
  variables,
  queryOptions,
  fetchOptions,
}: UseOAIInfiniteQueryProps<
  TQueryKey,
  OAIParameters<TQueryKey, 'get'>,
  Omit<OAISchema['CursorPagedResponse'], 'content'> & {
    content: TContent[];
  },
  OAIApiError
>) {
  const uuidHeaderOption = useUserUUID();

  const query = useInfiniteQuery(
    variables ? [queryKey, variables] : queryKey,
    async ({ signal, pageParam }) => {
      type SuccessResponse = Omit<
        OAISchema['CursorPagedResponse'],
        'content'
      > & {
        content: TContent[];
      };

      if (isServer()) {
        return {} as SuccessResponse;
      }

      const data = await fetchOAIData<SuccessResponse>({
        queryKey,
        variables: {
          ...(variables || {}),
          ...((pageParam || {}) as Record<string, unknown>),
        },
        fetchOptions: {
          ...fetchOptions,
          signal,
          headers: {
            ...fetchOptions?.headers,
            'X-Client-Auth-Token': getSnowdonJwt(),
            ...uuidHeaderOption,
          },
        },
      });

      return data;
    },
    queryOptions,
  );

  return {
    ...query,
    fetchNextPage: () => {
      if (query.isFetching || !query.hasNextPage) {
        return;
      }

      void query.fetchNextPage();
    },
    pageParams: query.data?.pageParams,
    pages: query.data?.pages.reduce(
      (current, next) => current.concat(next.content),
      [] as TContent[],
    ),
  };
}

export function useIsReactQueryBusy() {
  const inProgressQueryCount = useIsFetching();
  const inProgressMutationCount = useIsMutating();
  return inProgressMutationCount > 0 || inProgressQueryCount > 0;
}

type CacheKeyType =
  | StorageKeyType
  | 'recommend-search-keywords'
  | 'marketplace-e-coupon-recommend-keywords'
  | 'management-date'
  | 'selected-coupon-id'
  | 'market-account-dev-data'
  | 'market-account-stg-data';

export function useSetCacheData<TData>() {
  const queryClient = useQueryClient();
  return (key: CacheKeyType, data: TData) =>
    queryClient.setQueryData(key, data);
}

export function useGetCacheData<TData>(key: CacheKeyType) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<TData>(key);
}

export function useCacheData<TData>(
  key: CacheKeyType,
  queryFn: QueryFunction<TData, CacheKeyType>,
  options?: UseQueryOptions<TData, unknown, TData, CacheKeyType>,
): [TData, (data: TData) => void] {
  const queryClient = useQueryClient();
  const { data } = useQuery(key, queryFn, {
    suspense: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    ...options,
  });

  return [data as TData, (newData) => queryClient.setQueryData(key, newData)];
}

export function useSyncStorageEffect(
  effect: (payload: string) => void | Promise<void>,
  key: StorageKeyType,
  options?: { blacklist: string[] },
) {
  const router = useRouter();
  const [payload, setPayload] = useCacheData(
    key,
    () => {
      if (isServer()) {
        return null;
      }

      const isBlacklist = options?.blacklist.reduce(
        (_, path) => router.pathname.includes(path),
        false,
      );

      if (isBlacklist) {
        return null;
      }

      const result = getStorageData(key);
      return result;
    },
    { refetchOnWindowFocus: 'always', refetchOnMount: 'always' },
  );

  React.useEffect(() => {
    if (!payload) {
      return;
    }

    void effect(payload);
    setPayload(null);
    removeStorageData(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);
}
